import { useMemo } from 'react'
import { UserX } from 'lucide-react'
import type { ColumnPinningState } from '@tanstack/react-table'

import { DataTable } from '@/components/ui/data-table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { AdminUser } from '@/hooks/use-admin-users'
import type { AdminUsersSort } from '@/lib/list-filters/admin-users'
import { EMPTY_STRING_SET } from '@/lib/empty-collections'
import { cn } from '@/lib/utils'
import type { ListSelection } from '@/lib/list-selection'
import { getAdminUsersColumns } from './users-table-columns'

type UserListTableProps = {
  users: AdminUser[]
  isLoading: boolean
  sortBy: AdminUsersSort
  onSortChange: (sort: AdminUsersSort) => void
  onUserClick: (userId: string) => void
  onEditUser: (userId: string, user?: AdminUser) => void
  highlightedUserIds?: ReadonlySet<string>
  selection?: ListSelection
}

const ADMIN_USERS_COLUMN_PINNING: ColumnPinningState = { right: ['actions'] }

const TABLE_SKELETON_ROWS = 8
const TABLE_SKELETON_COLUMNS = 6

function AdminUsersTableSkeleton({ ariaLabel = 'Loading users' }: { ariaLabel?: string }) {
  return (
    <div
      className="rounded-md border bg-card"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      <Table>
        <TableHeader>
          <TableRow>
            {Array.from({ length: TABLE_SKELETON_COLUMNS }).map((_, index) => (
              <TableHead key={index}>
                <Skeleton className="h-4 w-20" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: TABLE_SKELETON_ROWS }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <Skeleton className="size-8 rounded-full" />
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </div>
              </TableCell>
              {Array.from({ length: TABLE_SKELETON_COLUMNS - 1 }).map((_, cellIndex) => (
                <TableCell key={cellIndex}>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function AdminUsersEmptyState() {
  return (
    <div className="rounded-md border bg-card py-12 text-center">
      <UserX className="mx-auto mb-4 size-12 text-muted-foreground" />
      <h3 className="mb-2 text-lg font-semibold">No users found</h3>
      <p className="text-muted-foreground">
        Try adjusting your search criteria or create a new user.
      </p>
    </div>
  )
}

export function UserListTable({
  users,
  isLoading,
  sortBy,
  onSortChange,
  onUserClick,
  onEditUser,
  highlightedUserIds = EMPTY_STRING_SET,
  selection,
}: UserListTableProps) {
  const columns = useMemo(
    () =>
      getAdminUsersColumns({
        onView: onUserClick,
        onEdit: onEditUser,
        sortBy,
        onSortChange,
        selection,
        visibleIds: users.map((user) => user.id),
      }),
    [onUserClick, onEditUser, sortBy, onSortChange, selection, users],
  )

  if (isLoading) {
    return <AdminUsersTableSkeleton />
  }

  if (users.length === 0) {
    return <AdminUsersEmptyState />
  }

  return (
    <div className="rounded-md border bg-card">
      <DataTable
        columns={columns}
        data={users}
        initialColumnPinning={ADMIN_USERS_COLUMN_PINNING}
        onRowClick={(user) => onUserClick(user.id)}
        getRowClassName={(user) =>
          cn(
            highlightedUserIds.has(user.id) && 'ring-2 ring-primary ring-offset-2 animate-pulse',
          )
        }
      />
    </div>
  )
}
